import React, { useState, useEffect } from "react";
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
import {
    Package,
    Calendar,
    AlertTriangle,
    TrendingUp,
    Eye,
    Filter,
    Search,
    RefreshCw,
    Clock,
    CheckCircle2,
    XCircle,
    AlertCircle,
    Pill
} from "lucide-react";

const FIFOStockViewer = () => {
    const [medicines, setMedicines] = useState([]);
    const [selectedMedicine, setSelectedMedicine] = useState(null);
    const [stockData, setStockData] = useState(null);
    const [alerts, setAlerts] = useState({ low_stock_medicines: [], near_expiry_batches: [] });
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("info");

    useEffect(() => {
        fetchMedicines();
        fetchAlerts();
    }, []);

    const fetchMedicines = async () => {
        try {
            const response = await axiosInstance.get("/medicines");
            setMedicines(response.data.medicines);
        } catch (error) {
            console.error("Error fetching medicines:", error);
            showAlertMessage("Error fetching medicines", "error");
        }
    };

    const fetchAlerts = async () => {
        try {
            const response = await axiosInstance.get("/purchases/fifo/alerts");
            setAlerts(response.data);
        } catch (error) {
            console.error("Error fetching alerts:", error);
        }
    };

    const fetchStockData = async (medicineId) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/purchases/fifo/stock/${medicineId}`);
            setStockData(response.data);
            setSelectedMedicine(medicines.find(m => m.id === parseInt(medicineId)));
        } catch (error) {
            console.error("Error fetching stock data:", error);
            showAlertMessage("Error fetching FIFO stock data", "error");
        } finally {
            setLoading(false);
        }
    };

    const showAlertMessage = (message, type) => {
        setAlertMessage(message);
        setAlertType(type);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
    };

    const getBatchStatusColor = (batch) => {
        if (batch.days_to_expiry <= 0) return "bg-red-100 text-red-800 border-red-200";
        if (batch.days_to_expiry <= 30) return "bg-yellow-100 text-yellow-800 border-yellow-200";
        if (batch.days_to_expiry <= 90) return "bg-blue-100 text-blue-800 border-blue-200";
        return "bg-green-100 text-green-800 border-green-200";
    };

    const getBatchStatusIcon = (batch) => {
        if (batch.days_to_expiry <= 0) return <XCircle className="h-4 w-4" />;
        if (batch.days_to_expiry <= 30) return <AlertTriangle className="h-4 w-4" />;
        if (batch.days_to_expiry <= 90) return <Clock className="h-4 w-4" />;
        return <CheckCircle2 className="h-4 w-4" />;
    };

    const filteredMedicines = medicines.filter(medicine =>
        medicine.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        medicine.generic_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="bg-blue-100 p-3 rounded-lg">
                                    <TrendingUp className="h-8 w-8 text-blue-600" />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">FIFO Stock Viewer</h1>
                                    <p className="text-gray-600 mt-1">First In First Out Inventory Management</p>
                                </div>
                            </div>
                            <button
                                onClick={fetchAlerts}
                                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Refresh Alerts
                            </button>
                        </div>
                    </div>

                    {/* Alert */}
                    {showAlert && (
                        <div className={`mb-6 p-4 rounded-lg border ${
                            alertType === "success" ? "bg-green-50 border-green-200 text-green-800" : 
                            alertType === "error" ? "bg-red-50 border-red-200 text-red-800" :
                            "bg-blue-50 border-blue-200 text-blue-800"
                        }`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <AlertCircle className="h-5 w-5 mr-2" />
                                    <span>{alertMessage}</span>
                                </div>
                                <button onClick={() => setShowAlert(false)}>
                                    <XCircle className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Medicine Selector */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <Pill className="h-5 w-5 mr-2 text-blue-600" />
                                    Select Medicine
                                </h2>
                                
                                {/* Search */}
                                <div className="relative mb-4">
                                    <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search medicines..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                {/* Medicine List */}
                                <div className="space-y-2 max-h-96 overflow-y-auto">
                                    {filteredMedicines.map((medicine) => (
                                        <button
                                            key={medicine.id}
                                            onClick={() => fetchStockData(medicine.id)}
                                            className={`w-full text-left p-3 rounded-lg border transition-colors ${
                                                selectedMedicine?.id === medicine.id
                                                    ? "bg-blue-50 border-blue-200 text-blue-900"
                                                    : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                                            }`}
                                        >
                                            <div className="font-medium">{medicine.name}</div>
                                            <div className="text-sm text-gray-600">{medicine.generic_name}</div>
                                            <div className="text-xs text-gray-500">{medicine.strength}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Alerts Summary */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <AlertTriangle className="h-5 w-5 mr-2 text-yellow-600" />
                                    Stock Alerts
                                </h3>
                                
                                <div className="space-y-4">
                                    <div className="bg-red-50 p-3 rounded-lg">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-red-800">Low Stock</span>
                                            <span className="text-lg font-bold text-red-600">
                                                {alerts.low_stock_medicines.length}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-yellow-50 p-3 rounded-lg">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-yellow-800">Near Expiry</span>
                                            <span className="text-lg font-bold text-yellow-600">
                                                {alerts.near_expiry_batches.length}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* FIFO Stock Details */}
                        <div className="lg:col-span-2">
                            {loading ? (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                                    <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-4" />
                                    <p className="text-gray-600">Loading FIFO stock data...</p>
                                </div>
                            ) : stockData ? (
                                <div className="space-y-6">
                                    {/* Stock Summary */}
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <Package className="h-5 w-5 mr-2 text-blue-600" />
                                            {stockData.medicine.name} - Stock Summary
                                        </h2>
                                        
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="bg-blue-50 p-4 rounded-lg text-center">
                                                <p className="text-sm text-blue-600 font-medium">Total Stock</p>
                                                <p className="text-2xl font-bold text-blue-700">
                                                    {stockData.total_stock_units}
                                                </p>
                                                <p className="text-xs text-blue-600">units</p>
                                            </div>
                                            
                                            <div className="bg-green-50 p-4 rounded-lg text-center">
                                                <p className="text-sm text-green-600 font-medium">Active Batches</p>
                                                <p className="text-2xl font-bold text-green-700">
                                                    {stockData.fifo_batches.length}
                                                </p>
                                                <p className="text-xs text-green-600">batches</p>
                                            </div>
                                            
                                            <div className="bg-yellow-50 p-4 rounded-lg text-center">
                                                <p className="text-sm text-yellow-600 font-medium">Next Expiry</p>
                                                <p className="text-2xl font-bold text-yellow-700">
                                                    {stockData.fifo_batches.length > 0 
                                                        ? Math.min(...stockData.fifo_batches.map(b => b.days_to_expiry))
                                                        : "N/A"
                                                    }
                                                </p>
                                                <p className="text-xs text-yellow-600">days</p>
                                            </div>
                                            
                                            <div className={`p-4 rounded-lg text-center ${
                                                stockData.stock_consistency 
                                                    ? "bg-green-50" 
                                                    : "bg-red-50"
                                            }`}>
                                                <p className={`text-sm font-medium ${
                                                    stockData.stock_consistency 
                                                        ? "text-green-600" 
                                                        : "text-red-600"
                                                }`}>
                                                    Data Integrity
                                                </p>
                                                <div className={`text-2xl font-bold ${
                                                    stockData.stock_consistency 
                                                        ? "text-green-700" 
                                                        : "text-red-700"
                                                }`}>
                                                    {stockData.stock_consistency ? (
                                                        <CheckCircle2 className="h-8 w-8 mx-auto" />
                                                    ) : (
                                                        <XCircle className="h-8 w-8 mx-auto" />
                                                    )}
                                                </div>
                                                <p className={`text-xs ${
                                                    stockData.stock_consistency 
                                                        ? "text-green-600" 
                                                        : "text-red-600"
                                                }`}>
                                                    {stockData.stock_consistency ? "Consistent" : "Mismatch"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* FIFO Batches */}
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                                            FIFO Batch Queue (Priority Order)
                                        </h3>
                                        
                                        {stockData.fifo_batches.length > 0 ? (
                                            <div className="space-y-3">
                                                {stockData.fifo_batches.map((batch, index) => (
                                                    <div
                                                        key={batch.batch_id}
                                                        className={`p-4 rounded-lg border-2 ${getBatchStatusColor(batch)}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center space-x-3">
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="bg-white px-2 py-1 rounded text-sm font-bold">
                                                                        #{index + 1}
                                                                    </span>
                                                                    {getBatchStatusIcon(batch)}
                                                                </div>
                                                                <div>
                                                                    <div className="font-medium">
                                                                        Batch: {batch.batch_number}
                                                                    </div>
                                                                    <div className="text-sm opacity-75">
                                                                        FIFO Priority: {batch.fifo_priority}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="text-right">
                                                                <div className="font-bold text-lg">
                                                                    {batch.remaining_units} units
                                                                </div>
                                                                <div className="text-sm opacity-75">
                                                                    {batch.remaining_quantity} packages
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 pt-3 border-t border-white">
                                                            <div>
                                                                <p className="text-xs opacity-75">Expiry Date</p>
                                                                <p className="font-medium">
                                                                    {new Date(batch.expiry_date).toLocaleDateString()}
                                                                </p>
                                                                <p className="text-xs">
                                                                    ({batch.days_to_expiry} days)
                                                                </p>
                                                            </div>
                                                            
                                                            <div>
                                                                <p className="text-xs opacity-75">Purchase Date</p>
                                                                <p className="font-medium">
                                                                    {new Date(batch.purchase_date).toLocaleDateString()}
                                                                </p>
                                                            </div>
                                                            
                                                            <div>
                                                                <p className="text-xs opacity-75">Selling Price</p>
                                                                <p className="font-medium">
                                                                    ₹{batch.selling_price_per_unit}/unit
                                                                </p>
                                                            </div>
                                                            
                                                            <div>
                                                                <p className="text-xs opacity-75">MRP</p>
                                                                <p className="font-medium">
                                                                    ₹{batch.mrp || "N/A"}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-8 text-gray-500">
                                                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                                <p>No active batches found for this medicine</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                                    <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        Select a Medicine
                                    </h3>
                                    <p className="text-gray-600">
                                        Choose a medicine from the list to view its FIFO stock details
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Alerts Details */}
                    {(alerts.low_stock_medicines.length > 0 || alerts.near_expiry_batches.length > 0) && (
                        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Low Stock Alerts */}
                            {alerts.low_stock_medicines.length > 0 && (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                    <h3 className="text-lg font-semibold text-red-900 mb-4 flex items-center">
                                        <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                                        Low Stock Medicines
                                    </h3>
                                    <div className="space-y-3">
                                        {alerts.low_stock_medicines.map((alert, index) => (
                                            <div key={index} className="bg-red-50 p-3 rounded-lg border border-red-200">
                                                <div className="font-medium text-red-900">
                                                    {alert.medicine_name}
                                                </div>
                                                <div className="text-sm text-red-700">
                                                    Current: {alert.current_stock} | Required: {alert.reorder_level}
                                                </div>
                                                <div className="text-xs text-red-600">
                                                    Shortage: {alert.shortage} units
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Near Expiry Alerts */}
                            {alerts.near_expiry_batches.length > 0 && (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                    <h3 className="text-lg font-semibold text-yellow-900 mb-4 flex items-center">
                                        <Clock className="h-5 w-5 mr-2 text-yellow-600" />
                                        Near Expiry Batches
                                    </h3>
                                    <div className="space-y-3">
                                        {alerts.near_expiry_batches.map((alert, index) => (
                                            <div key={index} className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                                <div className="font-medium text-yellow-900">
                                                    {alert.medicine_name}
                                                </div>
                                                <div className="text-sm text-yellow-700">
                                                    Batch: {alert.batch_number} | Stock: {alert.remaining_units} units
                                                </div>
                                                <div className="text-xs text-yellow-600">
                                                    Expires in: {alert.days_to_expiry} days
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Authenticated>
    );
};

export default FIFOStockViewer;
